/**
 * Image Utilities
 *
 * uploadImage()     — compresses the file and POSTs it to the backend.
 *                     Returns a hosted URL (never a base64 data: string).
 * compressImage()   — local helper that reduces file size before upload.
 *                     Not intended to be called directly by Slate nodes.
 */

import httpClient from '../../../services/modules/ReportBuilder/api/httpClient';

// ─── Compression helper (unchanged logic) ────────────────────────────────────
// Returns a Blob (not a data-URL) so we can send it efficiently.
const compressToBlob = async (
  file: File,
  maxWidth = 1200,
  quality = 0.82
): Promise<Blob> => {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let {width, height} = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx!.drawImage(img, 0, 0, width, height);

        // jpeg for photos, webp for transparency — fallback to jpeg
        const outputType =
          file.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas toBlob failed'));
          },
          outputType,
          quality
        );
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

/**
 * Uploads an image File to the backend and returns the permanent hosted URL.
 *
 * @param file - The raw File object from an <input type="file"> or drag-drop
 * @returns A URL string like "http://localhost:4000/uploads/img_xxx.jpg"
 *
 * IMPORTANT: The returned URL (not a base64 string) should be stored in
 * the Slate node's `url` field.
 */
export const uploadImage = async (file: File): Promise<string> => {
  const blob = await compressToBlob(file);
  const formData = new FormData();
  formData.append('image', blob, file.name);

  const response = await httpClient.post<{url: string}>(
    '/api/images/upload',
    formData,
    {
      headers: {'Content-Type': 'multipart/form-data'}
    }
  );

  return response.data.url;
};

/**
 * Legacy helper kept for internal use (e.g. watermark generation).
 * Do NOT store its output in Slate nodes — use uploadImage() instead.
 * @deprecated for Slate node storage — use uploadImage()
 */
export const compressImage = async (
  file: File,
  maxWidth = 800,
  quality = 0.7
): Promise<string> => {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let {width, height}: {width: number; height: number} = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx!.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};
